/**
 * Client-side image compression — runs in the browser via canvas.
 *
 * Why this exists:
 * Smartphone photos are typically 4-8 MB each (HEIC/JPEG at native sensor
 * resolution). Six of them = 30-50 MB to upload, which on residential / 4G
 * connections takes 30-90 seconds. That's longer than most ingress / CDN
 * timeouts, so the browser reports the post as `TypeError: Failed to fetch`.
 *
 * Compressing each image to max 1600px wide + JPEG 80% quality typically
 * shrinks a 6 MB photo to ~300 KB while staying perfectly readable for ID
 * verification. Total payload drops from ~30 MB to ~2 MB → upload completes
 * in seconds, well under any ingress limit.
 *
 * PDFs and any non-image file types are returned as-is.
 */

const COMPRESSIBLE_MIME = /^image\/(jpe?g|png|webp|heic|heif)$/i;

/**
 * Compress an image File. Returns a NEW File (renamed to .jpg) or the
 * original file unchanged if it's not an image or already small.
 *
 * @param {File} file
 * @param {Object} opts
 * @param {number} [opts.maxWidth=1600] — longest edge in px
 * @param {number} [opts.quality=0.8]   — JPEG quality 0–1
 * @param {number} [opts.skipBelowBytes=400_000] — don't bother compressing tiny files
 */
export async function compressImage(file, opts = {}) {
  if (!file) return file;
  if (!COMPRESSIBLE_MIME.test(file.type)) return file;
  if (file.size <= (opts.skipBelowBytes ?? 400_000)) return file;

  const maxWidth = opts.maxWidth ?? 1600;
  const quality = opts.quality ?? 0.8;

  try {
    const bitmap = await readImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxWidth / Math.max(width, height));
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))), 'image/jpeg', quality)
    );
    // Only swap in the compressed version if it's actually smaller.
    if (blob.size >= file.size) return file;
    const baseName = (file.name || 'image').replace(/\.[a-z0-9]+$/i, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (_err) {
    // If canvas / decode fails for any reason, fall back to the original file.
    return file;
  }
}

async function readImageBitmap(file) {
  // createImageBitmap is widely supported and decodes off-main-thread.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (_) {
      /* fall through to <img> decode */
    }
  }
  // Fallback: HTMLImageElement
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}
